import type {
  EventInput,
  EventOccurrence,
  EventRecord,
  EventType,
  ImportCommitRequest,
  ImportCommitResult,
  ImportPreviewResult,
  Person,
  Source,
} from "@shared/types";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
    credentials: "same-origin",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error || "Something went wrong");
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export { ApiError };

// --- Auth -------------------------------------------------------------

export const authApi = {
  session: () => request<{ authenticated: boolean }>("/auth/session"),
  login: (password: string) =>
    request<{ authenticated: boolean }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ authenticated: boolean }>("/auth/logout", { method: "POST" }),
};

// --- People -------------------------------------------------------------

export const peopleApi = {
  list: (includeArchived = false) =>
    request<{ people: Person[] }>(`/people${includeArchived ? "?includeArchived=true" : ""}`),
  create: (input: { name: string; color: string; initial?: string }) =>
    request<{ person: Person }>("/people", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<Person>) =>
    request<{ ok: true }>(`/people/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  archive: (id: string) => request<{ ok: true }>(`/people/${id}`, { method: "DELETE" }),
};

// --- Event types ----------------------------------------------------------

export const eventTypesApi = {
  list: (includeArchived = false) =>
    request<{ eventTypes: EventType[] }>(
      `/event-types${includeArchived ? "?includeArchived=true" : ""}`,
    ),
  create: (input: { name: string; color: string }) =>
    request<{ eventType: EventType }>("/event-types", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<EventType>) =>
    request<{ ok: true }>(`/event-types/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  archive: (id: string) => request<{ ok: true }>(`/event-types/${id}`, { method: "DELETE" }),
};

// --- Sources ----------------------------------------------------------------

export const sourcesApi = {
  list: () => request<{ sources: Source[] }>("/sources"),
  create: (input: { label: string; url?: string; personId?: string | null }) =>
    request<{ source: Source }>("/sources", { method: "POST", body: JSON.stringify(input) }),
};

// --- Events -----------------------------------------------------------------

export const eventsApi = {
  list: (params: { start: string; end: string; personIds?: string[]; eventTypeIds?: string[] }) => {
    const search = new URLSearchParams({ start: params.start, end: params.end });
    if (params.personIds?.length) search.set("personIds", params.personIds.join(","));
    if (params.eventTypeIds?.length) search.set("eventTypeIds", params.eventTypeIds.join(","));
    return request<{ events: EventOccurrence[] }>(`/events?${search.toString()}`);
  },
  search: (query: string) =>
    request<{ events: EventOccurrence[] }>(`/events?search=${encodeURIComponent(query)}`),
  get: (id: string) => request<{ event: EventRecord }>(`/events/${id}`),
  create: (input: EventInput) =>
    request<{ id: string }>("/events", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, input: EventInput, scope: "occurrence" | "series", occurrenceStart?: string) =>
    request<{ ok: true }>(`/events/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ event: input, scope, occurrenceStart }),
    }),
  remove: (id: string, scope: "occurrence" | "series", occurrenceStart?: string) => {
    const search = new URLSearchParams({ scope });
    if (occurrenceStart) search.set("occurrenceStart", occurrenceStart);
    return request<{ ok: true }>(`/events/${id}?${search.toString()}`, { method: "DELETE" });
  },
  duplicate: (id: string) => request<{ id: string }>(`/events/${id}/duplicate`, { method: "POST" }),
};

// --- Import -------------------------------------------------------------

export const importApi = {
  preview: async (file: File, durationMinutes?: number): Promise<ImportPreviewResult> => {
    const form = new FormData();
    form.append("file", file);
    if (durationMinutes) form.append("durationMinutes", String(durationMinutes));
    const res = await fetch("/api/import/preview", { method: "POST", body: form, credentials: "same-origin" });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new ApiError(res.status, body.error || "Import preview failed");
    }
    return res.json();
  },
  commit: (input: ImportCommitRequest) =>
    request<ImportCommitResult>("/import/commit", { method: "POST", body: JSON.stringify(input) }),
};
